import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { ContractObservationViewStore } from "../../store";
import { ContractObservationView } from "../view/ContractObservationView";
import { ContractObservationUpdateStore } from "../../store";
import { ContractObservationUpdate } from "../update/ContractObservationUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type ContractObservationModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const ContractObservationModalContent: React.FC<ContractObservationModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new ContractObservationUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new ContractObservationViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <ContractObservationView
            contractObservationViewStore={
              storeInfo.store as ContractObservationViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <ContractObservationUpdate
            contractObservationUpdateStore={
              storeInfo.store as ContractObservationUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
